import { Grid, List, ListItem } from '@mui/material';
import { debounce, zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core';
import * as zxcvbnCommonPackage from '@zxcvbn-ts/language-common';
import * as zxcvbnEnPackage from '@zxcvbn-ts/language-en';
import { useEffect, useState } from 'react';

const minScore = 3;

interface PasswordStrengthMeterProps {
  password: string;
  handleScoreRequirementMet: (valid: boolean) => void;
}

export function PasswordStrengthMeter({
  password,
  handleScoreRequirementMet,
}: PasswordStrengthMeterProps) {
  const options = {
    translations: zxcvbnEnPackage.translations,
    graphs: zxcvbnCommonPackage.adjacencyGraphs,
    dictionary: {
      ...zxcvbnCommonPackage.dictionary,
      ...zxcvbnEnPackage.dictionary,
    },
  };

  zxcvbnOptions.setOptions(options);

  const [passwordResult, setPasswordResult] = useState(zxcvbn(''));

  useEffect(() => {
    const getPasswordResult = () => {
      const result = zxcvbn(password);
      setPasswordResult(result);
    };
    const debouncedZxcvbn = debounce(getPasswordResult, 0, false);
    debouncedZxcvbn();
  }, [password]);

  function scoreRequirementMet(score: number) {
    return score >= minScore;
  }

  useEffect(() => {
    handleScoreRequirementMet(scoreRequirementMet(passwordResult.score));
  }, [handleScoreRequirementMet, passwordResult.score]);

  function setStrengthMeterColor(score: number) {
    switch (score) {
      case 0: {
        return 'red';
      }
      case 1: {
        return 'red';
      }
      case 2: {
        return 'red';
      }
      case 3: {
        return 'orange';
      }
      case 4: {
        return 'green';
      }
      default:
        return 'red';
    }
  }

  const suggestions = passwordResult.feedback.suggestions.map((suggestion) => (
    <ListItem
      key={suggestion}
      value={suggestion}
      sx={{ display: 'list-item', marginLeft: '20px' }}
      disablePadding
      dense
    >
      {suggestion}
    </ListItem>
  ));

  const strength = {
    0: 'Weak',
    1: 'Weak',
    2: 'Weak',
    3: 'Fair',
    4: 'Strong',
  };

  const progressStyle = { height: '10px' };

  function getProgressWidth(score: number) {
    let resultWidth: number;
    if (score <= 2) {
      resultWidth = 100 / 3;
    } else if (score === 3) {
      resultWidth = (100 / 3) * 2;
    } else {
      resultWidth = 100;
    }
    const resultWidthString = resultWidth.toString();
    return `${resultWidthString}%`;
  }

  return (
    <Grid size={6}>
      <div hidden={password.length <= 3}>
        <div>
          <b>Strength:</b> {strength[passwordResult?.score]}
        </div>
        <div className="progress" style={progressStyle}>
          <div
            style={{
              width: getProgressWidth(passwordResult?.score),
              background: setStrengthMeterColor(passwordResult?.score),
              height: '10px',
            }}
            className="progress-bar"
            role="progressbar"
            aria-label="Password Strength"
            aria-valuenow={passwordResult?.score}
            aria-valuemin={1}
            aria-valuemax={4}
          />
        </div>
        <div className="mb-2">{passwordResult?.feedback.warning}</div>
        <div className="pl-8">
          <List sx={{ listStyleType: 'disc' }}>{suggestions}</List>
        </div>
      </div>
    </Grid>
  );
}

export default PasswordStrengthMeter;
